import React from 'react';
import _ from 'lodash';
import { Button, Icon } from '@brighterion/br-ui';
import { callAll, handleKeyEvent, handleOnClickEvent } from 'Utils';
import clsx from 'clsx';
import styles from './MultiOptionDropdown.css';

type MultiOptionDropdownProps = Omit<React.ComponentProps<typeof Button>, 'color'> & {
    id: string;
    baseText: string;
    options: MenuItem[];
    alignment: 'right' | 'left';
    showArrows?: boolean;
    iconElements?: boolean;
    horizontalRule?: boolean;
    callback?: () => void;
};

const MultiOptionDropdown = ({
    id,
    baseText,
    showArrows,
    alignment,
    options,
    iconElements,
    horizontalRule,
    callback,
    ...rest
}: MultiOptionDropdownProps) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [expanded, setExpanded] = React.useState(false);

    React.useEffect(() => {
        const listener = (event: MouseEvent) => {
            if (ref.current && !ref.current.contains(event.target as HTMLDivElement)) {
                setExpanded(false);
            }
        };

        document.addEventListener('mousedown', listener);
        return () => {
            document.removeEventListener('mousedown', listener);
        };
    }, [ref]);

    const handleBaseButtonState = () => {
        setExpanded(!expanded);
        // only call the callback when open the menu
        if (callback && !expanded) {
            callback();
        }
    };

    return (
        <div className={styles.multiOptionDropdownWrapper} ref={ref}>
            <Button
                id={_.uniqueId(id)}
                onClick={handleOnClickEvent(handleBaseButtonState)}
                onKeyDown={handleKeyEvent(handleBaseButtonState)}
                className={styles.multiOptionDropdownBaseButton}
                size='small'
                aria-expanded={expanded}
                {...rest}
            >
                <span className={styles.baseButtonText}> {baseText}</span>
                {showArrows && (
                    <Icon
                        name={expanded ? 'arrow-up-1' : 'arrow-down-1'}
                        size='sm'
                        className={clsx(
                            rest?.variant === 'primary' ? styles.primaryBaseButtonIcon : styles.baseButtonIcon,
                            rest?.variant === 'tertiary' && styles.tertiaryIconStyle
                        )}
                    />
                )}
            </Button>

            {expanded && (
                <div
                    className={clsx(
                        iconElements ? styles.dropdownMenu : styles.optionContainer,
                        alignment === 'right' && styles.optionContainerRightAdjusted
                    )}
                >
                    {options.map(
                        (option) =>
                            option.visibility && (
                                <React.Fragment key={_.uniqueId()}>
                                    {option.horizontalRule && (
                                        <div className={styles.horizontalRule} aria-hidden='true' />
                                    )}
                                    <Button
                                        iconName={option.iconName}
                                        className={clsx(
                                            option.iconElement ? styles.dropdownItem : styles.optionIconButton
                                        )}
                                        variant='tertiary'
                                        onClick={callAll(
                                            handleBaseButtonState,
                                            handleOnClickEvent(option.handleNavigation)
                                        )}
                                        onKeyDown={callAll(
                                            handleBaseButtonState,
                                            handleKeyEvent(option.handleNavigation)
                                        )}
                                    >
                                        <>
                                            {option.iconElement}
                                            {option.displayText}
                                        </>
                                    </Button>
                                </React.Fragment>
                            )
                    )}
                </div>
            )}
        </div>
    );
};

export default MultiOptionDropdown;
